import React, { useEffect, useState } from "react";
import styles from "./Login.module.css";
import Navbar from "../../layouts/navbar/Navbar";
import Button from "../../components/custom-button/Button";
import Footer from "../../layouts/footer/Footer";
import { reactIcons } from "../../icons/reactIcons";
import { useNavigate } from "react-router-dom";
import { authApis } from "../../apis/authApis";
import { encryptAES } from "../../utils/encryption";
import { isOktaConfigured, oktaAuth } from "../../auth/oktaAuth";
import Loader from "../../components/custom-loader/Loader";

const OKTA_LOGIN_PENDING_KEY = "LVoktaLoginPending";

const isOktaLoginInProgress = () =>
  sessionStorage.getItem(OKTA_LOGIN_PENDING_KEY) === "true" ||
  Boolean(oktaAuth?.isLoginRedirect());

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [oktaLoading, setOktaLoading] = useState(isOktaLoginInProgress);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const handleOktaRedirect = async () => {
      if (!oktaAuth?.isLoginRedirect()) {
        sessionStorage.removeItem(OKTA_LOGIN_PENDING_KEY);
        setOktaLoading(false);
        return;
      }

      setErrorMsg("");
      setOktaLoading(true);

      try {
        const { tokens } = await oktaAuth.token.parseFromUrl();
        oktaAuth.tokenManager.setTokens(tokens);

        const oktaToken = tokens.accessToken?.accessToken;

        if (!oktaToken) {
          throw new Error("Okta did not return an access token.");
        }

        const response = await authApis.oktaMe(oktaToken);
        const userDetails = response?.data ?? response;
        const accessToken =
          userDetails?.accessToken || userDetails?.accesstoken;

        if (!accessToken) {
          throw new Error("Okta user lookup did not return an access token.");
        }

        const firstName = String(userDetails?.firstName || "").trim();
        const lastName = String(userDetails?.lastName || "").trim();
        const emailAddress = String(userDetails?.email || "").trim();
        const fallbackName = emailAddress || "Okta User";
        const name = `${firstName} ${lastName}`.trim() || fallbackName;
        const loginUserDetails = { ...userDetails, accessToken };

        localStorage.setItem("LVaccessToken", accessToken);
        localStorage.setItem("LVoktaAuth", "true");
        localStorage.setItem("LVFname", name);
        localStorage.setItem("LVuser", JSON.stringify(loginUserDetails));
        sessionStorage.removeItem(OKTA_LOGIN_PENDING_KEY);
        oktaAuth.tokenManager.clear();

        navigate("/", { replace: true });
      } catch (error: any) {
        console.error("Okta login error:", error);
        oktaAuth.tokenManager.clear();
        localStorage.removeItem("LVaccessToken");
        localStorage.removeItem("LVoktaAuth");
        localStorage.removeItem("LVFname");
        localStorage.removeItem("LVuser");
        sessionStorage.removeItem(OKTA_LOGIN_PENDING_KEY);

        if (isMounted) {
          setErrorMsg(error?.message || "Okta login failed");
        }
      } finally {
        if (isMounted) {
          setOktaLoading(false);
        }
      }
    };

    handleOktaRedirect();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    try {
      const encryptedEmail = encryptAES(email);
      const encryptedPassword = encryptAES(password);

      const res = await authApis.login({
        email: encryptedEmail,
        password: encryptedPassword,
      });

      console.log("res", res);

      const token = res?.data?.accessToken;
      const name = `${res?.data?.firstName} ${res?.data?.lastName}`;

      if (!res?.success) {
        setErrorMsg(res?.data?.message);
        return;
      }

      localStorage.setItem("LVaccessToken", token);
      localStorage.setItem("LVFname", name);
      localStorage.setItem("LVuser", JSON.stringify(res.data));

      navigate("/", { replace: true });
    } catch (error: any) {
      console.log(error);
      setErrorMsg(error?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOktaLogin = async () => {
    if (!oktaAuth || !isOktaConfigured) {
      setErrorMsg("Okta login is not configured.");
      return;
    }

    setErrorMsg("");
    setOktaLoading(true);
    sessionStorage.setItem(OKTA_LOGIN_PENDING_KEY, "true");

    try {
      await oktaAuth.signInWithRedirect();
    } catch (error: any) {
      console.error("Okta redirect error:", error);
      sessionStorage.removeItem(OKTA_LOGIN_PENDING_KEY);
      setErrorMsg(error?.message || "Unable to start Okta login");
      setOktaLoading(false);
    }
  };

  if (oktaLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.loginPage}>
      <Navbar />

      <div className={styles.loginContainer}>
        <h2 className={styles.loginTitle}>Inspector Portal</h2>
        <p className={styles.loginSubtitle}>Log in to submit reports</p>

        <form className={styles.loginForm} onSubmit={handleSubmit}>
          <label>
            Email Address <span className={styles.required}>*</span>
          </label>
          <input
            type="email"
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label>
            Password <span className={styles.required}>*</span>
          </label>
          <div className={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <span
              className={styles.toggleIcon}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? (
                <reactIcons.EyeIcon style={{ fontSize: "27px" }} />
              ) : (
                <reactIcons.EyeOffIcon style={{ fontSize: "27px" }} />
              )}
            </span>
          </div>

          {errorMsg && <p className={styles.errorText}>{errorMsg}</p>}

          <div className={styles.loginAction}>
            <Button
              label={loading ? "Logging in.." : "Login"}
              type="submit"
              disabled={!email || !password || loading}
            />
          </div>

          <div className={styles.loginDivider}>
            <span>or</span>
          </div>

          <Button
            label="Sign in with Okta"
            type="button"
            onClick={handleOktaLogin}
            disabled={oktaLoading || loading}
          />
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
